import { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { useReportBlockNodeBuilder } from '@va/dashboard/shared/report-block-nodes';
import { useTranslate } from '@va/localization';
import { AppliedFilters } from '@va/shared/feature-filters';
import { DataViewOption } from '@va/types/report-block';
import { WebsiteType } from '@va/types/website';
import { DataTableV8, useControlledTableState } from '@va/ui/components/data-table';
import { getReportBlockPaginationId, ReportBlock, useReportBlockCtx } from '@va/ui/components/report-block';
import { ReportBlockNodeIds } from '@va/ui/components/report-block/nodes';
import { useCallback, useMemo } from 'react';
import { OrderType } from '../agency-white-label-apiClient';
import useAgencyWhiteLabelContext from '../AgencyWhiteLabelContext';
import { useManageWhiteLabelColumns } from './useManageWhiteLabelColumns';

const MANAGE_WL_RB = 'manage-wl';

const ManageWhiteLabelTable = () => {
  const translate = useTranslate();
  const buildReportBlockNode = useReportBlockNodeBuilder();

  const visualizationComponents = useMemo(
    () => ({
      [DataViewOption.table]: Table,
    }),
    [],
  );

  return (
    <ReportBlock
      id={MANAGE_WL_RB}
      title={translate('standalone.accountSettings.whiteLabelWebsites.title')}
      nodes={{
        filterButton: buildReportBlockNode(ReportBlockNodeIds.FILTER_BUTTON),
        columnVisibility: buildReportBlockNode(ReportBlockNodeIds.COLUMN_VISIBILITY),
        appliedFilters: <AppliedFilters />,
        pagination: <div id={getReportBlockPaginationId(MANAGE_WL_RB)} />,
      }}
      visualization={{ selectedView: DataViewOption.table }}
      visualizationComponents={visualizationComponents}
    />
  );
};

export default ManageWhiteLabelTable;

const Table = () => {
  const { isLoading, websites, websiteFilterQuery, setWebsiteFilterQuery, totalWebsitesCount } =
    useAgencyWhiteLabelContext();
  const { paginationId } = useReportBlockCtx();

  const { pagination, setPagination, sorting, setSorting, defaultSorting } = useControlledTableState<WebsiteType>(
    {
      pagination: { pageSize: websiteFilterQuery.length },
      defaultSorting: [{ id: 'url', desc: false }],
    },
    MANAGE_WL_RB,
  );
  const columns = useManageWhiteLabelColumns();

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updater) => {
      if (typeof updater !== 'function') return;

      const updatedPagination = updater(pagination);
      setPagination(updatedPagination);
      setWebsiteFilterQuery((prevFilters) => ({
        ...prevFilters,
        start: (updatedPagination?.pageIndex ?? 0) * (updatedPagination?.pageSize ?? 0),
        length: updatedPagination?.pageSize ?? 10,
      }));
    },
    [pagination, setPagination, setWebsiteFilterQuery],
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      if (typeof updater !== 'function') return;
      const updatedSorting = updater(sorting);
      setSorting(updatedSorting);
      setWebsiteFilterQuery((prevFilters) => ({
        ...prevFilters,
        order: updatedSorting?.[0]?.desc ? OrderType.DESC : OrderType.ASC,
      }));
    },
    [setSorting, setWebsiteFilterQuery, sorting],
  );

  return (
    <DataTableV8<WebsiteType>
      id='manage-wl-table'
      isLoading={isLoading}
      state={{ pagination, sorting }}
      paginationContainerId={paginationId}
      onPaginationChange={handlePaginationChange}
      onSortingChange={handleSortingChange}
      rowCount={totalWebsitesCount}
      columns={columns}
      data={websites}
      defaultSorting={defaultSorting}
    />
  );
};
